import { SOCIAL_MEDIA_LOOKUP_VALUE, Social, SocialKeys, socialMediaUrlMap } from '@ag-common-lib/public-api';
import { Injectable } from '@angular/core';
import { BaseFormService } from '../../../../utils/base-form-service';

@Injectable()
export class SocialFormService extends BaseFormService<Social> {
  readonly socialMediaUrlMap = socialMediaUrlMap;

  constructor() {
    super();
  }

  getFormData = (social: Partial<Social>) => {
    this.formData = new Proxy(social, {
      set: (target, prop, value, receiver) => {
        const prevValue = target[prop];

        if (value !== prevValue) {
          this.formChangesDetector.handleChange(prop, value, prevValue);
          Reflect.set(target, prop, value, receiver);
          const mediaType = this.formData?.[SocialKeys.socialMedia];
          const accountId = this.formData?.[SocialKeys.accountId];
          const accountType = this.formData?.[SocialKeys.accountType];

          switch (prop) {
            case SocialKeys.socialMedia:
              const startUrl = socialMediaUrlMap[value];
              switch (value) {
                case 'FB':
                case 'TW':
                case 'TT':
                  this._normalizeUrl(startUrl, this.normalizedAccountId(value, accountId));
                  break;
                case 'IG':
                  const normalizedAccountId = this.normalizedAccountId(mediaType, value);
                  this._normalizeUrl(startUrl, normalizedAccountId);
                  break;
                case 'YT':
                case 'LI':
                  this._normalizeUrl(startUrl, accountId, accountType);
                  break;
              }
              break;

            case SocialKeys.accountId:
              // this.handleAccountId(value, mediaType); // it's broke personal and business instagram links
              const normilizedAccountId = this.normalizedAccountId(mediaType, value);
              this._normalizeUrl(socialMediaUrlMap[mediaType], normilizedAccountId, accountType);
              break;

            case SocialKeys.accountType:
              this._normalizeUrl(socialMediaUrlMap[mediaType], accountId, value);
              break;
          }
        }
        return true;
      },
    });

    return this.formData;
  };

  extractAccountIdFromPastedText = (pastedText: string, mediaType?: string): string => {
    const trimmedText = pastedText?.trim() ?? '';
    if (!trimmedText) {
      return '';
    }

    if (mediaType === SOCIAL_MEDIA_LOOKUP_VALUE.TT) {
      const tikTokUserName = this.extractTikTokUserName(trimmedText);
      if (tikTokUserName) {
        return tikTokUserName;
      }
    }

    const pathOnly = trimmedText.split(/[?#]/)[0].replace(/\/+$/, '');
    const lastSlashIndex = pathOnly.lastIndexOf('/');
    if (lastSlashIndex === -1) {
      return this.ensureTikTokHandlePrefix(pathOnly, mediaType);
    }

    return this.ensureTikTokHandlePrefix(pathOnly.substring(lastSlashIndex + 1), mediaType);
  };

  private extractTikTokUserName = (pastedText: string): string | null => {
    const tikTokUserName = pastedText.match(/tiktok\.com\/(@[^/?#]+)/i)?.[1];
    if (!tikTokUserName) {
      return null;
    }

    return tikTokUserName;
  };

  private ensureTikTokHandlePrefix = (userName?: string, mediaType?: string): string => {
    if (mediaType !== SOCIAL_MEDIA_LOOKUP_VALUE.TT || !userName || userName.startsWith('@')) {
      return userName ?? '';
    }

    return `@${userName}`;
  };

  private normalizedAccountId = (mediaType: string, accountId?: string) => {
    if (mediaType === 'IG') {
      // if user add 'ad' sign to the username as @mention
      return accountId?.startsWith('@') ? accountId.slice(1) : accountId;
    }
    if (mediaType === SOCIAL_MEDIA_LOOKUP_VALUE.TT) {
      return this.ensureTikTokHandlePrefix(accountId, mediaType);
    }
    return accountId;
  };

  private handleAccountId = (accountId: string, mediaType) => {
    switch (mediaType) {
      case 'IG':
        if (!accountId) {
          return accountId;
        }

        const value = accountId?.trim();

        if (value.startsWith('@')) {
          return value;
        }
        this.formData[SocialKeys.accountId] = `@${value}`;
        return `@${value}`;

      default:
        return accountId;
    }
  };

  private _normalizeUrl = (url: string, accountId?: string, subFolder?: string) => {
    const urlStr = url;
    const subFolderStr = subFolder ?? '';
    const accountIdStr = accountId ?? '';

    Object.assign(this.formData, {
      [SocialKeys.url]: `${urlStr}${subFolderStr}${accountIdStr}`,
    });
  };
}
